import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

export type CommunityArticle = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  cover_image_url: string | null;
  author_name: string | null;
  author_id: string | null;
  category: string;
  tags: string[];
  is_featured: boolean;
  status: "draft" | "published" | "archived";
  published_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
};

export const ARTICLE_CATEGORY_LABELS: Record<string, string> = {
  general_news: "News",
  market_insights: "Market Insights",
  buying_guides: "Buying Guides",
  selling_tips: "Selling Tips",
  grading_guides: "Grading Guides",
  featured_seller: "Featured Seller",
  partner_spotlight: "Partner Spotlight",
  event_recap: "Event Recap",
  set_reviews: "Set Reviews",
};

export async function fetchPublishedArticles(limit = 24): Promise<CommunityArticle[]> {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from("community_articles")
    .select("*")
    .eq("status", "published")
    .or(`published_at.is.null,published_at.lte.${nowIso}`)
    .order("is_featured", { ascending: false })
    .order("published_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as CommunityArticle[];
}

export async function fetchFeaturedArticles(limit = 3): Promise<CommunityArticle[]> {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from("community_articles")
    .select("*")
    .eq("status", "published")
    .eq("is_featured", true)
    .or(`published_at.is.null,published_at.lte.${nowIso}`)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as CommunityArticle[];
}

export async function fetchArticleBySlug(slug: string): Promise<CommunityArticle | null> {
  const { data, error } = await supabase
    .from("community_articles")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) return null;
  return (data as CommunityArticle) ?? null;
}

const ArticleInput = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().min(2).max(240),
  slug: z.string().trim().min(2).max(240).regex(/^[a-z0-9-]+$/i),
  excerpt: z.string().trim().max(600).optional().nullable(),
  content: z.string().trim().max(80000).optional().nullable(),
  cover_image_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  author_name: z.string().trim().max(120).optional().nullable(),
  category: z.string().trim().min(1).max(60),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  is_featured: z.boolean().default(false),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  published_at: z.string().optional().nullable().or(z.literal("")),
  seo_title: z.string().trim().max(240).optional().nullable(),
  seo_description: z.string().trim().max(320).optional().nullable(),
  display_order: z.number().int().min(0).max(10000).default(0),
});

export const listAllArticles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("community_articles")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as CommunityArticle[];
  });

export const upsertArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ArticleInput.parse(d))
  .handler(async ({ data, context }) => {
    const clean = (v?: string | null) => (v ? v : null);
    const payload = {
      ...data,
      cover_image_url: clean(data.cover_image_url),
      published_at:
        data.published_at ||
        (data.status === "published" ? new Date().toISOString() : null),
      author_id: context.userId,
    };
    const { id, ...rest } = payload;
    if (id) {
      const { error } = await context.supabase
        .from("community_articles")
        .update(rest as never)
        .eq("id", id);
      if (error) throw new Error(error.message);
      return { ok: true, id };
    }
    const { data: row, error } = await context.supabase
      .from("community_articles")
      .insert(rest as never)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const deleteArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("community_articles")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
